import React, {useCallback, useEffect, useRef, useState} from 'react';
import NewJobDetailHeader from './new-job-detail-header/NewJobDetailHeader';
import NewJobDetailTabs from './new-job-detail-tabs/NewJobDetailTabs';
import './NewJobDetail.scss';
import {t} from 'i18next';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import {CRITERIA} from './issue-coding-type-header/IssueCodingTypeHeader';
import NewJobConfirmationModal from './new-job-confirmation-modal/NewJobConfirmationModal';
import {
  useCreateAIReviewJob,
  useGetAIReviewJobDetails,
  useGetProtocolNames,
  useGetProtocolVersionDetails,
  useUpdateAIReviewJob,
} from '../../../../../hooks/ai/review/useReview';
import {
  convertAppliedProtocolToManual,
  hideToast,
  initializeJobCreateProtocol,
  resetEditMode,
  resetFormData,
  resetProtocol,
  setCloneJobName,
  setDefineCriteria,
  setFormData,
  setInitialFormData,
  setInitialProtocol,
  setProtocolVersionBaseline,
  setJobType,
  showToast,
  syncProtocolToFormData,
} from '../../../../../redux/slice/aiReviewSliceV2';
import type {JobDetailData as V2JobDetailData} from '../../../../../redux/slice/aiReviewSliceV2';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {ToastType} from '../../../../../shared/constants';
import {isEqual} from 'lodash';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {
  buildCreateJobPayload,
  buildUpdateJobPayload,
} from './buildCreateJobPayload';
import {
  mapJobDetailsToEditState,
  isReviewProtocolInactive,
  getProtocolIdForInactivityCheck,
} from './mapJobDetailsToEditState';
import {canSubmitCreateJob} from './canSubmitCreateJob';
import {AI_REVIEW_JOB_STATUS} from '../../constant/AiReviewConstants';
import {mapProtocolVersionDetailsToFormData} from '../../protocolIntelligenceV2/utils/protocolVersionPayload';

/** @deprecated V1 shape — kept for aiReviewSlice and clone flow until Phase 2 */
export interface JobDetailDataLegacy {
  jobName: string;
  description: string;
  selectedGroup: import('../../../../../shared/types').ISelectItem | null;
}

/** @deprecated V1 shape — kept for aiReviewSlice and clone flow until Phase 2 */
export interface JobDetailFormDataLegacy {
  jobId?: number;
  jobDetail: JobDetailDataLegacy;
  contextAndCriteria: {
    context: string;
    issueCodes: import('../../../../../api/types/aiReview.type').IssueCode[];
    relevanceCriteria: string;
  };
  sourceSelection: number;
  sourceSelectionMode: import('./source-selection-step/SourceSelectionStep').SourceSelectionMode;
  sourceSelectionName?: string;
  totalDocs?: number | null;
  groundTruthTagID?: number | null;
  reviewProtocolID?: number | null;
  parentProtocolID?: number | null;
  isProtocolModified: boolean;
  parentReviewJobID?: number | null;
  isManual?: boolean;
}

// Re-export legacy names used by aiReviewSlice
export type JobDetailData = JobDetailDataLegacy;
export type JobDetailFormData = JobDetailFormDataLegacy;

const NewJobDetail = () => {
  const formData = useSelector((state: RootState) => state.aiReviewV2.formData);
  const protocol = useSelector((state: RootState) => state.aiReviewV2.protocol);
  const jobType = useSelector((state: RootState) => state.aiReviewV2.jobType);
  const initialFormData = useSelector(
    (state: RootState) => state.aiReviewV2.initialFormData
  );
  const initialProtocol = useSelector(
    (state: RootState) => state.aiReviewV2.initialProtocol
  );
  const {toast} = useSelector((state: RootState) => state.aiReviewV2);
  const cloneJobName = useSelector(
    (state: RootState) => state.aiReviewV2.cloneJobName
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const store = useStore();
  const [searchParams] = useSearchParams();
  const editJobId = searchParams.get('jobId');
  const isEditFlow = Boolean(editJobId);
  const isCloneFlow = Boolean(cloneJobName);
  const hasHydratedEditRef = useRef(false);
  const hasConvertedInactiveProtocolRef = useRef(false);
  const {mutate: createAIReviewJob} = useCreateAIReviewJob();
  const {mutate: updateAIReviewJob} = useUpdateAIReviewJob();
  const {
    data: editJobDetails,
    isError: isEditJobDetailsError,
    isLoading: isEditJobDetailsLoading,
  } = useGetAIReviewJobDetails({jobId: editJobId!}, isEditFlow);
  const {
    data: protocolNames,
    isSuccess: isProtocolNamesSuccess,
    isFetching: isProtocolNamesFetching,
    isFetchedAfterMount: isProtocolNamesFetchedAfterMount,
  } = useGetProtocolNames(isEditFlow || isCloneFlow);
  const areProtocolNamesCurrent =
    isProtocolNamesSuccess &&
    isProtocolNamesFetchedAfterMount &&
    !isProtocolNamesFetching;
  const shouldLoadProtocolVersionBaseline =
    !formData.isManual &&
    Boolean(formData.jobId || formData.parentReviewJobID) &&
    Boolean(
      formData.parentProtocolVersionID ?? formData.reviewProtocolVersionID
    );
  const protocolVersionBaselineId =
    formData.parentProtocolVersionID ?? formData.reviewProtocolVersionID;
  const {data: protocolVersionBaselineDetails} = useGetProtocolVersionDetails(
    protocolVersionBaselineId,
    shouldLoadProtocolVersionBaseline
  );
  const [showNewJobConfirmationModal, setShowNewJobConfirmationModal] =
    useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const hasJobName = Boolean(formData?.jobDetail?.jobName?.trim());
  const isSaveDraftDisabled = !hasJobName;
  const isSubmitDisabled = !canSubmitCreateJob(
    formData,
    jobType,
    protocol,
    isEditFlow
  );

  useEffect(() => {
    const {cloneJobName} = (store.getState() as RootState).aiReviewV2;
    if (cloneJobName || isEditFlow) {
      return;
    }

    dispatch(resetFormData());
    dispatch(resetProtocol());
    dispatch(initializeJobCreateProtocol());
    dispatch(resetEditMode());
    dispatch(setDefineCriteria(CRITERIA.MANUAL));
    dispatch(setCloneJobName(''));
  }, [dispatch, store, isEditFlow]);

  useEffect(() => {
    if (!isEditFlow || hasHydratedEditRef.current) {
      return;
    }

    if (isEditJobDetailsError) {
      dispatch(
        showToast({
          title: t('common.error'),
          message: t('notifications.messages.generalError'),
          status: [ToastType.Error],
        })
      );
      navigate('/AI/Review');
      return;
    }

    if (!editJobDetails) {
      return;
    }

    const mapped = mapJobDetailsToEditState(editJobDetails, Number(editJobId));
    dispatch(setJobType(mapped.jobType));
    dispatch(setFormData(mapped.formData));
    dispatch(setInitialFormData(mapped.formData));
    dispatch(setInitialProtocol(mapped.protocol));
    dispatch(
      setDefineCriteria(
        mapped.formData.isManual ? CRITERIA.MANUAL : CRITERIA.SAVED
      )
    );
    if (mapped.formData.isManual) {
      hasConvertedInactiveProtocolRef.current = true;
    }
    hasHydratedEditRef.current = true;
  }, [
    dispatch,
    editJobDetails,
    editJobId,
    isEditFlow,
    isEditJobDetailsError,
    navigate,
  ]);

  useEffect(() => {
    if (hasConvertedInactiveProtocolRef.current) {
      return;
    }
    if (isEditFlow && formData.jobId !== Number(editJobId)) {
      return;
    }
    if (formData.isManual) {
      hasConvertedInactiveProtocolRef.current = true;
      return;
    }
    if (!areProtocolNamesCurrent) {
      return;
    }
    if (!isEditFlow && !isCloneFlow) {
      return;
    }

    const protocolIdToCheck = getProtocolIdForInactivityCheck(
      Boolean(formData.isProtocolModified),
      protocol.protocolId,
      formData.parentProtocolID
    );

    if (!isReviewProtocolInactive(protocolIdToCheck, protocolNames ?? [])) {
      return;
    }

    hasConvertedInactiveProtocolRef.current = true;
    dispatch(convertAppliedProtocolToManual());
  }, [
    areProtocolNamesCurrent,
    dispatch,
    formData.isManual,
    formData.isProtocolModified,
    formData.parentProtocolID,
    isCloneFlow,
    isEditFlow,
    protocol.protocolId,
    protocolNames,
  ]);

  useEffect(() => {
    if (
      !protocolVersionBaselineDetails ||
      !shouldLoadProtocolVersionBaseline ||
      !protocolVersionBaselineId
    ) {
      return;
    }

    dispatch(
      setProtocolVersionBaseline(
        mapProtocolVersionDetailsToFormData(protocolVersionBaselineDetails, {
          // A modified job's reviewProtocolID is the saved-as protocol. The
          // reset baseline must retain the original parent association.
          protocolId: formData.isProtocolModified
            ? (formData.parentProtocolID ?? protocol.protocolId)
            : protocol.protocolId,
          protocolVersionId: protocolVersionBaselineId,
        })
      )
    );
  }, [
    dispatch,
    formData.isProtocolModified,
    formData.parentProtocolID,
    protocol.protocolId,
    protocolVersionBaselineDetails,
    protocolVersionBaselineId,
    shouldLoadProtocolVersionBaseline,
  ]);

  const hasUnsavedChanges =
    !isEqual(formData, initialFormData) || !isEqual(protocol, initialProtocol);
  const lastInsideTargetRef = useRef<HTMLElement | null>(null);

  const handleJobDetailChange = useCallback(
    (jobDetail: V2JobDetailData) => {
      dispatch(setFormData({jobDetail}));
    },
    [dispatch]
  );

  const triggerToast = () => {
    dispatch(
      showToast({
        title: t('ai.review.newJobDetail.draftToastTitle'),
        message: t('ai.review.newJobDetail.draftToastDesc', {
          value: formData?.jobDetail?.jobName,
        }),
        status: [ToastType.Success],
      })
    );
    dispatch(setCloneJobName(''));
    dispatch(resetFormData());
    dispatch(resetProtocol());
    dispatch(initializeJobCreateProtocol());
    dispatch(resetEditMode());
    dispatch(setDefineCriteria(CRITERIA.MANUAL));
  };

  const onExitWithoutSaving = () => {
    navigate('/AI/Review');
    dispatch(setCloneJobName(''));
    dispatch(resetFormData());
    dispatch(resetProtocol());
    dispatch(initializeJobCreateProtocol());
    dispatch(resetEditMode());
    dispatch(setDefineCriteria(CRITERIA.MANUAL));
  };

  const onSaveDraft = () => {
    if (isSaveDraftDisabled) return;

    dispatch(syncProtocolToFormData());
    const state = store.getState() as RootState;
    const mutationOptions = {
      onSuccess: (data: {errorMessage?: string}) => {
        if (data?.errorMessage) {
          dispatch(
            showToast({
              title: t('common.error'),
              message: data.errorMessage,
              status: [ToastType.Error],
            })
          );
          return;
        }
        triggerToast();
        navigate('/AI/Review');
      },
      onError: (error: {
        response?: {data?: {detail?: string}};
        message?: string;
      }) => {
        dispatch(
          showToast({
            title: t('common.error'),
            message:
              error?.response?.data?.detail ??
              error?.message ??
              t('common.error'),
            status: [ToastType.Error],
          })
        );
      },
    };

    if (state.aiReviewV2.formData.jobId) {
      updateAIReviewJob(
        buildUpdateJobPayload(
          state.aiReviewV2.formData,
          state.aiReviewV2.jobType,
          AI_REVIEW_JOB_STATUS.draft,
          state.aiReviewV2.protocol,
          state.aiReviewV2.initialProtocol
        ),
        mutationOptions
      );
      return;
    }

    createAIReviewJob(
      buildCreateJobPayload(
        state.aiReviewV2.formData,
        state.aiReviewV2.jobType,
        AI_REVIEW_JOB_STATUS.draft,
        state.aiReviewV2.protocol,
        state.aiReviewV2.initialProtocol
      ),
      mutationOptions
    );
  };

  const onSubmitNewJob = () => {
    if (isSubmitDisabled) return;
    dispatch(syncProtocolToFormData());
    setShowNewJobConfirmationModal(true);
  };
  const onCloseNewJobConfirmationModal = () =>
    setShowNewJobConfirmationModal(false);

  useEffect(() => {
    if (!hasUnsavedChanges || showNewJobConfirmationModal) return;

    const handleOutsideClick = (e: MouseEvent) => {
      if (e.button === 2) return;

      const wizardEl = document.querySelector('.create-job-detail');
      if (!wizardEl) return;

      const target = e.target as HTMLElement;

      if (wizardEl.contains(target)) {
        lastInsideTargetRef.current = target;
        return;
      }

      const isMuiPortal =
        target.closest('[role="listbox"]') ||
        target.closest('[role="option"]') ||
        target.closest('.MuiPopover-root') ||
        target.closest('.MuiMenu-root') ||
        target.closest('[role="dialog"]') ||
        target.closest('.modal') ||
        target.closest('.MuiDialog-root') ||
        target.closest('.MuiBackdrop-root') ||
        target.classList.contains('MuiBackdrop-root');

      if (isMuiPortal && lastInsideTargetRef.current) {
        return;
      }

      lastInsideTargetRef.current = null;
      e.stopPropagation();
      e.preventDefault();
      setShowExitModal(true);
    };
    document.addEventListener('mousedown', handleOutsideClick, true);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick, true);
    };
  }, [hasUnsavedChanges, showNewJobConfirmationModal]);

  return (
    <div className="create-job-detail" data-testid="new-job-detail-wrapper">
      <NewJobDetailHeader
        showExitModal={showExitModal}
        setShowExitModal={setShowExitModal}
        onSaveDraft={onSaveDraft}
        onSubmit={onSubmitNewJob}
        onExitWithoutSaving={onExitWithoutSaving}
        hasUnsavedChanges={hasUnsavedChanges}
        isSaveDraftDisabled={isSaveDraftDisabled}
        isSubmitDisabled={isSubmitDisabled}
        saveDraftLabel={
          isEditFlow ? t('common.save') : t('ai.review.newJobDetail.saveDraft')
        }
      />
      {isEditFlow && isEditJobDetailsLoading && !formData.jobId ? (
        <ArrLoadingSpinner />
      ) : null}
      <div className="create-job-detail__section">
        <NewJobDetailTabs
          jobDetail={formData.jobDetail}
          onJobDetailChange={handleJobDetailChange}
          isCreatingGroup={isCreatingGroup}
          setIsCreatingGroup={setIsCreatingGroup}
          isDraftProtocolChangedAfterJobModified={Boolean(
            editJobDetails?.isDraftProtocolChangedAfterJobModified
          )}
        />
      </div>
      <NewJobConfirmationModal
        showNewJobConfirmationModal={showNewJobConfirmationModal}
        onCloseNewJobConfirmationModal={onCloseNewJobConfirmationModal}
      />
      {toast?.open && (
        <ArrToastNotification
          title={toast.title}
          message={toast.message}
          status={toast.status}
          onClose={() => dispatch(hideToast())}
          data-testid="ar-review-toast-global"
          aria-label={toast.title}
        />
      )}
    </div>
  );
};

export default NewJobDetail;
